import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { Badge } from "@/components/ui/Badge";

interface JobStats {
  total: number;
  finished: number;
  failed: number;
  scheduled: number;
  ready: number;
  claimed: number;
  blocked: number;
  recurring: number;
  processes: number;
}

interface FailedJob {
  id: number;
  job_id: number;
  job_class: string;
  queue: string;
  error: { message?: string; error_class?: string };
  failed_at: string;
}

interface RecentJob {
  id: number;
  class_name: string;
  queue_name: string;
  priority: number;
  finished_at: string | null;
  scheduled_at: string | null;
  created_at: string;
}

interface RecurringTask {
  id: number;
  key: string;
  class_name: string;
  command: string | null;
  schedule: string;
  queue_name: string;
  priority: number;
}

interface ProcessInfo {
  id: number;
  kind: string;
  pid: number;
  hostname: string;
  last_heartbeat_at: string | null;
  created_at: string;
}

interface ClaimedJob {
  id: number;
  job_id: number;
  job_class: string;
  queue: string;
  process_id: number;
  claimed_at: string;
  created_at: string;
}

interface JobsData {
  stats: JobStats;
  claimed_jobs: ClaimedJob[];
  failed_jobs: FailedJob[];
  recent_jobs: RecentJob[];
  recurring_tasks: RecurringTask[];
  processes: ProcessInfo[];
}

interface JobDetail {
  id: number;
  class_name: string;
  queue_name: string;
  priority: number;
  active_job_id: string | null;
  arguments: Record<string, unknown>;
  scheduled_at: string | null;
  finished_at: string | null;
  created_at: string;
  status: string;
  error: {
    error_class?: string;
    message?: string;
    backtrace?: string[];
  } | null;
  failed_execution_id: number | null;
}

function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
}

type Tab = "overview" | "claimed" | "failed" | "recent" | "recurring" | "processes";

export function AdminJobsPage() {
  const [data, setData] = useState<JobsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>("overview");
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth("/admin/jobs");
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRetry = async (id: number) => {
    await fetchWithAuth(`/admin/jobs/${id}/retry`, { method: "POST" });
    setSelectedJobId(null);
    loadData();
  };

  const handleDiscard = async (id: number) => {
    await fetchWithAuth(`/admin/jobs/${id}/discard`, { method: "DELETE" });
    setSelectedJobId(null);
    loadData();
  };

  const handleRetryAll = async () => {
    await fetchWithAuth("/admin/jobs/retry_all", { method: "POST" });
    loadData();
  };

  const handleRunRecurring = async (id: number) => {
    await fetchWithAuth(`/admin/jobs/recurring/${id}/run`, { method: "POST" });
    loadData();
  };

  if (loading && !data) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (!data) return null;

  const tabs: { key: Tab; label: string }[] = [
    { key: "overview", label: "Apžvalga" },
    { key: "claimed", label: `Vykdomos (${data.stats.claimed})` },
    { key: "failed", label: `Nepavykę (${data.stats.failed})` },
    { key: "recent", label: "Paskutiniai" },
    { key: "recurring", label: "Periodiniai" },
    { key: "processes", label: `Procesai (${data.stats.processes})` },
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <Link to="/admin" className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        ← Admin valdymo skydas
      </Link>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Foninės užduotys</h1>
          <p className="mt-1 text-sm text-gray-500">SolidQueue valdymas</p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="rounded-lg bg-primary-800 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-900 disabled:opacity-50"
        >
          {loading ? "Kraunama..." : "Atnaujinti"}
        </button>
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard label="Iš viso" value={data.stats.total} />
        <StatCard label="Baigtos" value={data.stats.finished} color="green" />
        <StatCard label="Nepavykę" value={data.stats.failed} color="red" />
        <StatCard label="Suplanuotos" value={data.stats.scheduled} color="blue" />
        <StatCard label="Laukiančios" value={data.stats.ready} color="yellow" />
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b border-gray-200">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.key
                ? "border-b-2 border-primary-800 text-primary-800"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {tab === "overview" && <OverviewTab data={data} />}
        {tab === "claimed" && (
          <ClaimedTab
            jobs={data.claimed_jobs}
            onJobClick={(jobId) => setSelectedJobId(jobId)}
          />
        )}
        {tab === "failed" && (
          <FailedTab
            jobs={data.failed_jobs}
            onRetry={handleRetry}
            onDiscard={handleDiscard}
            onRetryAll={handleRetryAll}
            onJobClick={(jobId) => setSelectedJobId(jobId)}
          />
        )}
        {tab === "recent" && (
          <RecentTab
            jobs={data.recent_jobs}
            onJobClick={(jobId) => setSelectedJobId(jobId)}
          />
        )}
        {tab === "recurring" && <RecurringTab tasks={data.recurring_tasks} onRun={handleRunRecurring} />}
        {tab === "processes" && <ProcessesTab processes={data.processes} />}
      </div>

      {selectedJobId !== null && (
        <JobDetailModal
          jobId={selectedJobId}
          onClose={() => setSelectedJobId(null)}
          onRetry={handleRetry}
          onDiscard={handleDiscard}
        />
      )}
    </div>
  );
}

function JobDetailModal({
  jobId,
  onClose,
  onRetry,
  onDiscard,
}: {
  jobId: number;
  onClose: () => void;
  onRetry: (id: number) => void;
  onDiscard: (id: number) => void;
}) {
  const [detail, setDetail] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  useEffect(() => {
    setLoading(true);
    fetchWithAuth(`/admin/jobs/${jobId}`)
      .then(async (res) => {
        if (res.ok) setDetail(await res.json());
      })
      .finally(() => setLoading(false));
  }, [jobId]);

  const formatCopyText = (job: JobDetail): string => {
    const lines: string[] = [];
    lines.push(`## ${job.status === "failed" ? "Failed" : ""} Job: ${job.class_name}`);
    lines.push("");
    lines.push(`**Status:** ${statusLabel(job.status)}`);
    lines.push(`**Queue:** ${job.queue_name}`);
    lines.push(`**Priority:** ${job.priority}`);
    if (job.finished_at) lines.push(`**Finished at:** ${job.finished_at}`);
    lines.push(`**Created at:** ${job.created_at}`);
    if (job.active_job_id) lines.push(`**Active Job ID:** ${job.active_job_id}`);
    lines.push("");
    lines.push("### Arguments");
    lines.push("```json");
    lines.push(JSON.stringify(job.arguments?.arguments ?? job.arguments, null, 2));
    lines.push("```");

    if (job.error) {
      lines.push("");
      lines.push("### Error");
      lines.push(`**Class:** ${job.error.error_class || "Unknown"}`);
      lines.push(`**Message:** ${job.error.message || "No message"}`);
      if (job.error.backtrace && job.error.backtrace.length > 0) {
        lines.push("");
        lines.push("### Backtrace");
        lines.push("```");
        lines.push(job.error.backtrace.join("\n"));
        lines.push("```");
      }
    }

    return lines.join("\n");
  };

  const handleCopy = async () => {
    if (!detail) return;
    await navigator.clipboard.writeText(formatCopyText(detail));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {loading ? "Kraunama..." : detail?.class_name || "Užduoties detalės"}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          )}

          {!loading && detail && (
            <div className="space-y-6">
              {/* Status and metadata */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Būsena:</span>{" "}
                  <StatusBadge status={detail.status} />
                </div>
                <div>
                  <span className="text-gray-500">Eilė:</span>{" "}
                  <Badge variant="default">{detail.queue_name}</Badge>
                </div>
                <div>
                  <span className="text-gray-500">Prioritetas:</span>{" "}
                  <span className="font-medium">{detail.priority}</span>
                </div>
                <div>
                  <span className="text-gray-500">Job ID:</span>{" "}
                  <span className="font-mono text-xs">{detail.active_job_id || "—"}</span>
                </div>
                <div>
                  <span className="text-gray-500">Sukurta:</span>{" "}
                  <span className="font-medium">
                    {new Date(detail.created_at).toLocaleString("lt-LT")}
                  </span>
                </div>
                {detail.finished_at && (
                  <div>
                    <span className="text-gray-500">Baigta:</span>{" "}
                    <span className="font-medium">
                      {new Date(detail.finished_at).toLocaleString("lt-LT")}
                    </span>
                  </div>
                )}
                {detail.scheduled_at && (
                  <div>
                    <span className="text-gray-500">Suplanuota:</span>{" "}
                    <span className="font-medium">
                      {new Date(detail.scheduled_at).toLocaleString("lt-LT")}
                    </span>
                  </div>
                )}
              </div>

              {/* Arguments */}
              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-700">Argumentai</h3>
                <pre className="max-h-48 overflow-auto rounded-lg bg-gray-50 p-3 text-xs text-gray-800">
                  {JSON.stringify(detail.arguments, null, 2)}
                </pre>
              </div>

              {/* Error section */}
              {detail.error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <h3 className="mb-2 text-sm font-semibold text-red-800">Klaida</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-red-600">Klasė:</span>{" "}
                      <span className="font-mono font-medium text-red-900">
                        {detail.error.error_class || "—"}
                      </span>
                    </div>
                    <div>
                      <span className="text-red-600">Žinutė:</span>{" "}
                      <span className="text-red-900">{detail.error.message || "—"}</span>
                    </div>
                    {detail.error.backtrace && detail.error.backtrace.length > 0 && (
                      <div>
                        <span className="text-red-600">Backtrace:</span>
                        <pre className="mt-1 max-h-64 overflow-auto rounded bg-red-100 p-3 text-xs text-red-900">
                          {detail.error.backtrace.join("\n")}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && detail && (
          <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
            <button
              onClick={handleCopy}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              {copied ? "Nukopijuota!" : "Kopijuoti AI"}
            </button>
            <div className="flex gap-2">
              {detail.status === "failed" && detail.failed_execution_id && (
                <>
                  <button
                    onClick={() => onRetry(detail.failed_execution_id!)}
                    className="rounded-lg bg-primary-800 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-900"
                  >
                    Pakartoti
                  </button>
                  <button
                    onClick={() => onDiscard(detail.failed_execution_id!)}
                    className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-red-700"
                  >
                    Atmesti
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                Uždaryti
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, "success" | "danger" | "info" | "warning" | "default"> = {
    finished: "success",
    failed: "danger",
    scheduled: "info",
    claimed: "warning",
    ready: "default",
    blocked: "warning",
  };
  return <Badge variant={variants[status] || "default"}>{statusLabel(status)}</Badge>;
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    finished: "Baigta",
    failed: "Nepavyko",
    scheduled: "Suplanuota",
    claimed: "Vykdoma",
    ready: "Laukia",
    blocked: "Užblokuota",
  };
  return labels[status] || status;
}

function StatCard({
  label,
  value,
  color = "gray",
}: {
  label: string;
  value: number;
  color?: string;
}) {
  const colorClasses: Record<string, string> = {
    gray: "bg-gray-50 border-gray-200",
    blue: "bg-blue-50 border-blue-200",
    green: "bg-green-50 border-green-200",
    red: "bg-red-50 border-red-200",
    yellow: "bg-yellow-50 border-yellow-200",
  };

  return (
    <div className={`rounded-xl border p-4 ${colorClasses[color] || colorClasses.gray}`}>
      <p className="text-sm text-gray-600">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

function OverviewTab({ data }: { data: JobsData }) {
  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="font-semibold text-gray-900">Sistemos būsena</h3>
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Aktyvūs procesai:</span>{" "}
            <span className="font-medium">{data.stats.processes}</span>
          </div>
          <div>
            <span className="text-gray-500">Periodinės užduotys:</span>{" "}
            <span className="font-medium">{data.stats.recurring}</span>
          </div>
          <div>
            <span className="text-gray-500">Vykdomos dabar:</span>{" "}
            <span className="font-semibold text-primary-800">{data.stats.claimed}</span>
            {data.claimed_jobs.length > 0 && (
              <div className="mt-1 text-xs text-gray-500">
                {data.claimed_jobs
                  .slice(0, 5)
                  .map((j) => j.job_class)
                  .join(", ")}
                {data.claimed_jobs.length > 5 && ` ir dar ${data.claimed_jobs.length - 5}`}
              </div>
            )}
          </div>
          <div>
            <span className="text-gray-500">Užblokuotos:</span>{" "}
            <span className="font-medium">{data.stats.blocked}</span>
          </div>
        </div>
      </div>

      {data.failed_jobs.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6">
          <h3 className="font-semibold text-red-900">
            Nepavykusios užduotys ({data.stats.failed})
          </h3>
          <ul className="mt-3 space-y-2">
            {data.failed_jobs.slice(0, 5).map((job) => (
              <li key={job.id} className="text-sm text-red-800">
                <span className="font-medium">{job.job_class}</span> &mdash;{" "}
                {job.error.message || "Nežinoma klaida"}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ClaimedTab({
  jobs,
  onJobClick,
}: {
  jobs: ClaimedJob[];
  onJobClick: (jobId: number) => void;
}) {
  if (jobs.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-gray-500">
        Šiuo metu vykdomų užduočių nėra
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-gray-200 text-gray-500">
          <tr>
            <th className="pb-2 pr-4">Užduotis</th>
            <th className="pb-2 pr-4">Eilė</th>
            <th className="pb-2 pr-4">Proceso ID</th>
            <th className="pb-2 pr-4">Pradėta</th>
            <th className="pb-2">Sukurta</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {jobs.map((job) => (
            <tr
              key={job.id}
              className="cursor-pointer hover:bg-gray-50"
              onClick={() => onJobClick(job.job_id)}
            >
              <td className="py-3 pr-4 font-medium text-gray-900">{job.job_class}</td>
              <td className="py-3 pr-4">
                <Badge variant="default">{job.queue}</Badge>
              </td>
              <td className="py-3 pr-4 text-gray-500">{job.process_id}</td>
              <td className="py-3 pr-4 text-gray-500">
                {new Date(job.claimed_at).toLocaleString("lt-LT")}
              </td>
              <td className="py-3 text-gray-500">
                {new Date(job.created_at).toLocaleString("lt-LT")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FailedTab({
  jobs,
  onRetry,
  onDiscard,
  onRetryAll,
  onJobClick,
}: {
  jobs: FailedJob[];
  onRetry: (id: number) => void;
  onDiscard: (id: number) => void;
  onRetryAll: () => void;
  onJobClick: (jobId: number) => void;
}) {
  if (jobs.length === 0) {
    return <p className="py-8 text-center text-sm text-gray-500">Nepavykusių užduočių nėra</p>;
  }

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button
          onClick={onRetryAll}
          className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
        >
          Pakartoti visas
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-gray-200 text-gray-500">
            <tr>
              <th className="pb-2 pr-4">Užduotis</th>
              <th className="pb-2 pr-4">Eilė</th>
              <th className="pb-2 pr-4">Klaida</th>
              <th className="pb-2 pr-4">Data</th>
              <th className="pb-2">Veiksmai</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {jobs.map((job) => (
              <tr
                key={job.id}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => onJobClick(job.job_id)}
              >
                <td className="py-3 pr-4 font-medium text-gray-900">{job.job_class}</td>
                <td className="py-3 pr-4">
                  <Badge variant="default">{job.queue}</Badge>
                </td>
                <td className="max-w-xs truncate py-3 pr-4 text-red-600">
                  {job.error.message || job.error.error_class || "—"}
                </td>
                <td className="py-3 pr-4 text-gray-500">
                  {new Date(job.failed_at).toLocaleString("lt-LT")}
                </td>
                <td className="flex gap-2 py-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRetry(job.id);
                    }}
                    className="text-primary-800 hover:underline"
                  >
                    Pakartoti
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDiscard(job.id);
                    }}
                    className="text-red-600 hover:underline"
                  >
                    Atmesti
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RecentTab({
  jobs,
  onJobClick,
}: {
  jobs: RecentJob[];
  onJobClick: (jobId: number) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-gray-200 text-gray-500">
          <tr>
            <th className="pb-2 pr-4">ID</th>
            <th className="pb-2 pr-4">Užduotis</th>
            <th className="pb-2 pr-4">Eilė</th>
            <th className="pb-2 pr-4">Būsena</th>
            <th className="pb-2">Sukurta</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {jobs.map((job) => (
            <tr
              key={job.id}
              className="cursor-pointer hover:bg-gray-50"
              onClick={() => onJobClick(job.id)}
            >
              <td className="py-3 pr-4 text-gray-500">{job.id}</td>
              <td className="py-3 pr-4 font-medium text-gray-900">{job.class_name}</td>
              <td className="py-3 pr-4">
                <Badge variant="default">{job.queue_name}</Badge>
              </td>
              <td className="py-3 pr-4">
                {job.finished_at ? (
                  <Badge variant="success">Baigta</Badge>
                ) : job.scheduled_at ? (
                  <Badge variant="info">Suplanuota</Badge>
                ) : (
                  <Badge variant="warning">Vykdoma</Badge>
                )}
              </td>
              <td className="py-3 text-gray-500">
                {new Date(job.created_at).toLocaleString("lt-LT")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecurringTab({ tasks, onRun }: { tasks: RecurringTask[]; onRun: (id: number) => void }) {
  if (tasks.length === 0) {
    return <p className="py-8 text-center text-sm text-gray-500">Periodinių užduočių nėra</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-gray-200 text-gray-500">
          <tr>
            <th className="pb-2 pr-4">Raktas</th>
            <th className="pb-2 pr-4">Klasė / Komanda</th>
            <th className="pb-2 pr-4">Tvarkaraštis</th>
            <th className="pb-2 pr-4">Eilė</th>
            <th className="pb-2 pr-4">Prioritetas</th>
            <th className="pb-2">Veiksmai</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {tasks.map((task) => (
            <tr key={task.id}>
              <td className="py-3 pr-4 font-medium text-gray-900">{task.key}</td>
              <td className="py-3 pr-4 text-gray-700">
                {task.class_name || task.command || "—"}
              </td>
              <td className="py-3 pr-4 text-gray-500">{task.schedule}</td>
              <td className="py-3 pr-4">
                <Badge variant="default">{task.queue_name || "default"}</Badge>
              </td>
              <td className="py-3 pr-4 text-gray-500">{task.priority}</td>
              <td className="py-3">
                <button
                  onClick={() => onRun(task.id)}
                  className="text-primary-800 hover:underline"
                >
                  Paleisti
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProcessesTab({ processes }: { processes: ProcessInfo[] }) {
  if (processes.length === 0) {
    return <p className="py-8 text-center text-sm text-gray-500">Aktyvių procesų nėra</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-gray-200 text-gray-500">
          <tr>
            <th className="pb-2 pr-4">Tipas</th>
            <th className="pb-2 pr-4">PID</th>
            <th className="pb-2 pr-4">Serveris</th>
            <th className="pb-2 pr-4">Paskutinis pulzas</th>
            <th className="pb-2">Paleistas</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {processes.map((proc) => (
            <tr key={proc.id}>
              <td className="py-3 pr-4 font-medium text-gray-900">{proc.kind}</td>
              <td className="py-3 pr-4 text-gray-500">{proc.pid}</td>
              <td className="py-3 pr-4 text-gray-500">{proc.hostname}</td>
              <td className="py-3 pr-4 text-gray-500">
                {proc.last_heartbeat_at
                  ? new Date(proc.last_heartbeat_at).toLocaleString("lt-LT")
                  : "—"}
              </td>
              <td className="py-3 text-gray-500">
                {new Date(proc.created_at).toLocaleString("lt-LT")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
